// packages/shared-components/src/components/ColorPicker/ColorPickerPanel.tsx

import { useState, useRef, useEffect, useCallback } from "react";
import { ColorPickerPanelProps } from "./types";
import { hexToRgb, rgbToHex, rgbToHsv, hsvToRgb } from "../../utils/color";
import styles from "./ColorPickerPanel.module.css";

/** 饱和度/色相拖拽时 onChange 防抖默认值（毫秒） */
export const COLOR_PICKER_DRAG_CHANGE_DEBOUNCE_MS = 150;

/**
 * 颜色选择面板
 *
 * HSV 颜色空间的详细选择界面（通常作为弹出内容使用）
 */
export function ColorPickerPanel({
  value,
  onChange,
  onPreviewChange,
  changeDebounceMs = 0,
  presetColors = [],
  showRainbowPreset = false,
  onRainbowPresetClick,
  saturationWidth = 240,
  saturationHeight = 200,
  hueWidth = 20,
  hueHeight = 200,
  disabled = false,
}: ColorPickerPanelProps) {
  const rgb = hexToRgb(value);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

  const [hue, setHue] = useState(hsv.h);
  const [saturation, setSaturation] = useState(hsv.s);
  const [brightness, setBrightness] = useState(hsv.v);
  const [inputMode, setInputMode] = useState<"hex" | "rgb">("rgb");

  const [isDraggingSaturation, setIsDraggingSaturation] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);

  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const pendingChangeRef = useRef<string | null>(null);
  const changeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  const onPreviewChangeRef = useRef(onPreviewChange);

  onChangeRef.current = onChange;
  onPreviewChangeRef.current = onPreviewChange;

  const clearChangeTimer = useCallback(() => {
    if (changeTimerRef.current) {
      clearTimeout(changeTimerRef.current);
      changeTimerRef.current = null;
    }
  }, []);

  const flushChange = useCallback(() => {
    clearChangeTimer();
    if (pendingChangeRef.current === null) return;
    const hex = pendingChangeRef.current;
    pendingChangeRef.current = null;
    onChangeRef.current?.(hex);
  }, [clearChangeTimer]);

  const emitColorChange = useCallback(
    (hex: string, immediate = false) => {
      if (disabled) return;

      onPreviewChangeRef.current?.(hex);

      if (!onChangeRef.current) return;

      if (immediate || changeDebounceMs <= 0) {
        clearChangeTimer();
        pendingChangeRef.current = null;
        onChangeRef.current(hex);
        return;
      }

      pendingChangeRef.current = hex;
      clearChangeTimer();
      changeTimerRef.current = setTimeout(() => {
        changeTimerRef.current = null;
        if (pendingChangeRef.current === null) return;
        const pending = pendingChangeRef.current;
        pendingChangeRef.current = null;
        onChangeRef.current?.(pending);
      }, changeDebounceMs);
    },
    [changeDebounceMs, clearChangeTimer, disabled]
  );

  // 同步外部颜色
  useEffect(() => {
    if (isDraggingSaturation || isDraggingHue) return;

    const rgb = hexToRgb(value);
    const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

    if (newHsv.v > 5 && newHsv.s > 5) {
      setHue(newHsv.h);
    }

    setSaturation(newHsv.s);
    setBrightness(newHsv.v);
  }, [value, isDraggingSaturation, isDraggingHue]);

  useEffect(
    () => () => {
      flushChange();
    },
    [flushChange]
  );

  const updateColor = useCallback(
    (h: number, s: number, v: number, immediate = false) => {
      const nextRgb = hsvToRgb(h, s, v);
      const hex = rgbToHex(nextRgb.r, nextRgb.g, nextRgb.b);
      emitColorChange(hex, immediate);
    },
    [emitColorChange]
  );

  // 饱和度面板拖拽
  const handleSaturationMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDraggingSaturation(true);
    handleSaturationMove(e);
  };

  const handleSaturationMove = (e: React.MouseEvent | MouseEvent) => {
    if (disabled || !saturationRef.current) return;

    const rect = saturationRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

    const newSaturation = (x / rect.width) * 100;
    const newBrightness = 100 - (y / rect.height) * 100;

    setSaturation(newSaturation);
    setBrightness(newBrightness);
    updateColor(hue, newSaturation, newBrightness);
  };

  // 色相条拖拽
  const handleHueMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDraggingHue(true);
    handleHueMove(e);
  };

  const handleHueMove = (e: React.MouseEvent | MouseEvent) => {
    if (disabled || !hueRef.current) return;

    const rect = hueRef.current.getBoundingClientRect();
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    const newHue = (y / rect.height) * 360;

    setHue(newHue);
    updateColor(newHue, saturation, brightness);
  };

  // 全局鼠标事件
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingSaturation) {
        handleSaturationMove(e);
      } else if (isDraggingHue) {
        handleHueMove(e);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingSaturation || isDraggingHue) {
        flushChange();
      }
      setIsDraggingSaturation(false);
      setIsDraggingHue(false);
    };

    if (isDraggingSaturation || isDraggingHue) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    flushChange,
    isDraggingSaturation,
    isDraggingHue,
    hue,
    saturation,
    brightness,
  ]);

  // RGB 输入处理
  const handleRgbChange = (channel: "r" | "g" | "b", value: string) => {
    if (disabled) return;
    const numValue = Math.max(0, Math.min(255, parseInt(value) || 0));
    const newRgb = { ...rgb, [channel]: numValue };
    const newHsv = rgbToHsv(newRgb.r, newRgb.g, newRgb.b);

    if (newHsv.v > 5 && newHsv.s > 5) {
      setHue(newHsv.h);
    }
    setSaturation(newHsv.s);
    setBrightness(newHsv.v);
    updateColor(newHsv.h, newHsv.s, newHsv.v, true);
  };

  // HEX 输入处理
  const handleHexChange = (inputValue: string) => {
    if (disabled) return;
    let hex = inputValue.replace(/[^0-9A-Fa-f]/g, "");
    if (hex.length > 6) hex = hex.slice(0, 6);

    if (hex.length === 6) {
      const fullHex = `#${hex}`;
      const rgb = hexToRgb(fullHex);
      const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

      if (newHsv.v > 5 && newHsv.s > 5) {
        setHue(newHsv.h);
      }
      setSaturation(newHsv.s);
      setBrightness(newHsv.v);
      emitColorChange(fullHex, true);
    }
  };

  // 预设颜色点击
  const handlePresetClick = (color: string) => {
    if (disabled) return;
    const rgb = hexToRgb(color);
    const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

    setHue(newHsv.h);
    setSaturation(newHsv.s);
    setBrightness(newHsv.v);
    emitColorChange(color, true);
  };

  // 彩色预设没有固定色号，仅通知外部切换 UI 状态。
  const handleRainbowPresetClick = () => {
    if (disabled) return;
    onRainbowPresetClick?.();
  };

  // 计算饱和度面板背景色
  const saturationBgColor = hsvToRgb(hue, 100, 100);
  const saturationBg = `rgb(${saturationBgColor.r}, ${saturationBgColor.g}, ${saturationBgColor.b})`;

  return (
    <div
      className={`${styles.panel}${disabled ? ` ${styles.panelDisabled}` : ""}`}
      aria-disabled={disabled}
    >
      {/* 颜色选择区 */}
      <div className={styles.pickerArea}>
        {/* 饱和度面板 */}
        <div
          ref={saturationRef}
          className={styles.saturationPanel}
          style={{
            backgroundColor: saturationBg,
            width: saturationWidth,
            height: saturationHeight,
          }}
          onMouseDown={handleSaturationMouseDown}
          aria-hidden={disabled}
        >
          <div className={styles.saturationWhite}>
            <div className={styles.saturationBlack}>
              <div
                className={styles.saturationPointer}
                style={{
                  left: `${saturation}%`,
                  top: `${100 - brightness}%`,
                }}
              >
                <div className={styles.saturationCircle} />
              </div>
            </div>
          </div>
        </div>

        {/* 色相条 */}
        <div
          ref={hueRef}
          className={styles.hueSlider}
          style={{
            width: hueWidth,
            height: hueHeight,
          }}
          onMouseDown={handleHueMouseDown}
        >
          <div
            className={styles.huePointer}
            style={{ top: `${(hue / 360) * 100}%` }}
          >
            <div className={styles.hueCircle} style={{ width: hueWidth }} />
          </div>
        </div>
      </div>

      {/* 颜色值输入 */}
      <div className={styles.inputRow}>
        <div className={styles.modeSwitch}>
          <button
            type="button"
            className={`${styles.modeButton} ${
              inputMode === "hex" ? styles.modeButtonActive : ""
            }`}
            onClick={() => setInputMode("hex")}
            disabled={disabled}
          >
            HEX
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${
              inputMode === "rgb" ? styles.modeButtonActive : ""
            }`}
            onClick={() => setInputMode("rgb")}
            disabled={disabled}
          >
            RGB
          </button>
        </div>

        {inputMode === "hex" ? (
          <div className={styles.hexInputWrapper}>
            <span className={styles.hexPrefix}>#</span>
            <input
              type="text"
              value={value.replace("#", "")}
              onChange={(e) => handleHexChange(e.target.value)}
              maxLength={6}
              disabled={disabled}
            />
          </div>
        ) : (
          <div className={styles.rgbInputWrapper}>
            <label className={styles.rgbChannel}>
              <span>R</span>
              <input
                type="number"
                value={rgb.r}
                onChange={(e) => handleRgbChange("r", e.target.value)}
                min="0"
                max="255"
                disabled={disabled}
              />
            </label>
            <label className={styles.rgbChannel}>
              <span>G</span>
              <input
                type="number"
                value={rgb.g}
                onChange={(e) => handleRgbChange("g", e.target.value)}
                min="0"
                max="255"
                disabled={disabled}
              />
            </label>
            <label className={styles.rgbChannel}>
              <span>B</span>
              <input
                type="number"
                value={rgb.b}
                onChange={(e) => handleRgbChange("b", e.target.value)}
                min="0"
                max="255"
                disabled={disabled}
              />
            </label>
          </div>
        )}
      </div>

      {/* 预设颜色 */}
      {(showRainbowPreset || presetColors.length > 0) && (
        <div className={styles.presetColors}>
          {showRainbowPreset ? (
            <button
              type="button"
              className={`${styles.presetColor} ${styles.presetColorRainbow}`}
              onClick={handleRainbowPresetClick}
              disabled={disabled}
              aria-label="彩色"
            />
          ) : null}
          {presetColors.map((color, index) => (
            <button
              key={index}
              type="button"
              className={styles.presetColor}
              style={{ backgroundColor: color }}
              onClick={() => handlePresetClick(color)}
              disabled={disabled}
              aria-label={color}
            />
          ))}
        </div>
      )}
    </div>
  );
}
